<script setup lang="ts">
import type { ContentAuthor } from '~/models/Content/ContentAuthor'
import type { ContentArticle } from '~/models/Content/ContentArticle'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'

const { article } = defineProps<{
  article: ContentArticle
}>()

const { t } = useI18n()
const { findContents } = useContentApi()

const { data: authors } = useQuery({
  staleTime: Infinity,
  queryKey: ['app-article-authors', article.locationId],
  queryFn: () => findContents<ContentAuthor>({
    contentIdCriterion: article.authors!.destinationContentIds,
    contentTypeCriterion: [ContentType.Author],
    subtreeCriterion: [Subtree.Authors],
    mainLocationCriterion: true,
  }),
  enabled: computed(() => !!article.authors?.destinationContentIds.length)
})
</script>

<template>
  <div class="flex h-full flex-col justify-center gap-6">
    <div
      v-if="article.overline"
      class="font-semibold tracking-wider uppercase"
      v-text="article.overline"
    />
    <div class="space-y-4">
      <h1 class="au-title font-bold" v-text="article.title" />
      <div v-if="article.intro" class="au-subtitle au-prose">
        <RichTextRenderer :text="article.intro" />
      </div>
    </div>
    <div
      v-if="authors && authors.length > 0"
      class="mt-auto"
    >
      {{ t('article.text') }}:
      <strong
        v-for="(author, index) in authors"
        :key="author.contentId"
      >
        {{ author.title }}<template v-if="index + 1 < authors.length">, </template>
      </strong>
    </div>
  </div>
</template>
